import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  REASONING_STEP_KINDS,
  type ReasoningApi,
  type ReasoningChainEntry,
  type ReasoningChainView,
  type ReasoningSession,
  type ReasoningStepKind,
} from "~/api/reasoningTypes";
import { Button } from "~/foundation/components/Button";
import type { ActionSpec } from "./usePendingAction";
import { type ChainEdit, useReasoningChain } from "./useReasoningChain";

/**
 * What "Continue from here" runs with — the tab's agent, model, effort, effort mode and
 * local-only switch — minus the session and the checkpoint the editor adds.
 */
export type ChainLaunch = Omit<
  Parameters<ReasoningApi["execute"]>[0],
  "sessionId" | "chainCheckpointId" | "commandId"
>;

type FormMode = "replace" | "insert" | "strike";
interface EditForm {
  mode: FormMode;
  /** The entry edited, struck or inserted after; null = insert at the start. */
  entryId: string | null;
  summary: string;
  stepKind: ReasoningStepKind;
  rationale: string;
}

/** `ReviseReasoningChainRequest`: a replacement or inserted step carries 1–4000 characters. */
const SUMMARY_LIMIT = 4000;
/** Longer entries (a model's thinking runs to pages) fold behind "Show the full text". */
const PREVIEW_CHARS = 600;
const isStepKind = (value: string | null): value is ReasoningStepKind =>
  REASONING_STEP_KINDS.includes(value as ReasoningStepKind);
const snippet = (text: string, max = 90) =>
  text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
const chip = "rounded border border-divider px-1.5 py-0.5 text-small";
const control = "rounded border border-divider bg-bg p-2";

/**
 * The rows a reader sees. The effective chain is the ordered list the next generation reads;
 * history is every entry in the order it was recorded, superseded and struck ones greyed. A
 * strike marker (the step that records a strike) is never a row of its own — its reason shows
 * on the entry it struck.
 */
export function chainRows(view: ReasoningChainView, history: boolean): ReasoningChainEntry[] {
  const byId = new Map(view.entries.map((entry) => [entry.id, entry]));
  if (!history)
    return view.effective
      .map((id) => byId.get(id))
      .filter((entry): entry is ReasoningChainEntry => entry !== undefined);
  const markers = new Set(
    view.entries.filter((entry) => entry.struck).map((entry) => entry.supersededByRecordId),
  );
  return view.entries.filter((entry) => !markers.has(entry.id));
}

/**
 * The session's chain of thought, editable: the steps people recorded, every candidate and the
 * thinking the model emitted (labelled unverified), in the order the next generation will read
 * them. Edit replaces an entry, Insert after adds one, Strike removes one — each as a new step
 * that supersedes the earlier entry, never a rewrite. Continue from here runs the tab's agent
 * with the chain pinned up to that entry.
 */
export function ReasoningChainEditor({
  session,
  disabled,
  act,
  launch,
  onRevised,
}: {
  session: ReasoningSession;
  /** The tab has a command in flight or journaled. */
  disabled: boolean;
  act: (action: ActionSpec) => Promise<unknown>;
  /** Undefined until the tab has a usable local agent; the continue buttons then say why. */
  launch: ChainLaunch | undefined;
  onRevised?: () => void;
}) {
  const { t } = useTranslation();
  const {
    chain,
    loading,
    error,
    conflict,
    pending,
    running,
    busy,
    refresh,
    revise,
    retry,
    discard,
  } = useReasoningChain({ sessionId: session.id, sessionRevision: session.revision, onRevised });
  const [history, setHistory] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);
  const [continuing, setContinuing] = useState(false);
  const frozen = disabled || busy || continuing;
  const rows = chain ? chainRows(chain, history) : [];
  const byRecord = new Map((chain?.entries ?? []).map((entry) => [entry.recordId, entry]));

  const open = (mode: FormMode, entry: ReasoningChainEntry | null) =>
    setForm({
      mode,
      entryId: entry?.id ?? null,
      summary: mode === "replace" && entry ? entry.summary : "",
      stepKind:
        mode === "replace" && entry && isStepKind(entry.stepKind)
          ? entry.stepKind
          : mode === "insert"
            ? "interpretation"
            : "revision",
      rationale: "",
    });
  const submit = async () => {
    if (!form) return;
    const rationale = form.rationale.trim() ? { rationale: form.rationale.trim() } : {};
    const edit: ChainEdit =
      form.mode === "strike"
        ? { operation: "strike", targetId: form.entryId ?? "", ...rationale }
        : form.mode === "replace"
          ? {
              operation: "replace",
              targetId: form.entryId ?? "",
              summary: form.summary.trim(),
              stepKind: form.stepKind,
              ...rationale,
            }
          : {
              operation: "insert",
              ...(form.entryId ? { afterId: form.entryId } : {}),
              summary: form.summary.trim(),
              stepKind: form.stepKind,
              ...rationale,
            };
    if (await revise(edit)) setForm(null);
  };
  const retryEdit = async () => {
    if (await retry()) setForm(null);
  };
  const continueFrom = async (entryId: string) => {
    if (!launch || frozen) return;
    setContinuing(true);
    try {
      await act({
        kind: "execute",
        input: { ...launch, sessionId: session.id, chainCheckpointId: entryId },
      });
      await refresh();
    } finally {
      setContinuing(false);
    }
  };

  const kindLabel = (entry: ReasoningChainEntry) =>
    entry.kind === "thinking"
      ? t("model thinking · unverified")
      : entry.kind === "candidate"
        ? t("candidate")
        : (entry.stepKind ?? t("step"));

  const editForm = (current: EditForm) => {
    const label =
      current.mode === "replace"
        ? t("Replace this entry")
        : current.mode === "insert"
          ? t("Insert a step")
          : t("Strike this entry");
    const canSubmit = !frozen && (current.mode === "strike" || current.summary.trim().length > 0);
    return (
      <div
        role="group"
        aria-label={label}
        className="mt-2 flex flex-col gap-2 rounded border border-divider p-2"
      >
        <p className="text-small font-semibold">{label}</p>
        {current.mode !== "strike" && (
          <>
            <label className="flex flex-col gap-1 text-small">
              {t("Chain step type")}
              <select
                aria-label={t("Chain step type")}
                value={current.stepKind}
                disabled={frozen}
                onChange={(e) =>
                  setForm({ ...current, stepKind: e.target.value as ReasoningStepKind })
                }
                className={control}
              >
                {REASONING_STEP_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {kind}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-small">
              {t("Chain step text")}
              <textarea
                aria-label={t("Chain step text")}
                maxLength={SUMMARY_LIMIT}
                rows={4}
                disabled={frozen}
                value={current.summary}
                onChange={(e) => setForm({ ...current, summary: e.target.value })}
                className={control}
              />
            </label>
          </>
        )}
        <label className="flex flex-col gap-1 text-small">
          {current.mode === "strike" ? t("Reason (optional)") : t("Why (optional)")}
          <textarea
            aria-label={current.mode === "strike" ? t("Strike reason") : t("Chain edit rationale")}
            maxLength={SUMMARY_LIMIT}
            rows={2}
            disabled={frozen}
            value={current.rationale}
            onChange={(e) => setForm({ ...current, rationale: e.target.value })}
            className={control}
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="primary" disabled={!canSubmit} onClick={() => void submit()}>
            {current.mode === "replace"
              ? t("Save replacement")
              : current.mode === "insert"
                ? t("Insert step")
                : t("Confirm strike")}
          </Button>
          <Button size="sm" disabled={running} onClick={() => setForm(null)}>
            {t("Cancel")}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <section
      aria-label={t("Reasoning chain editor")}
      className="flex flex-col gap-3 rounded border border-divider p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-semibold">{t("Chain of thought")}</h4>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1 text-small">
            <input
              type="checkbox"
              checked={history}
              onChange={(e) => setHistory(e.target.checked)}
            />
            {t("Show history")}
          </label>
          <Button size="sm" disabled={frozen} onClick={() => void refresh()}>
            {t("Reload chain")}
          </Button>
        </div>
      </div>
      <p className="text-small text-text-secondary">
        {t(
          "The next generation reads this chain: the steps people recorded, every candidate, and the thinking the model emitted. An edit never rewrites history — it appends a step that supersedes the earlier entry.",
        )}
      </p>
      {!launch && (
        <p className="text-small text-text-secondary">
          {t("Pick a local agent above to continue from an entry.")}
        </p>
      )}
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
      {conflict && <p role="status">{t("Someone changed this session; reloaded.")}</p>}
      {pending && !running && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={disabled} onClick={() => void retryEdit()}>
            {t("Retry the edit")}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              discard();
              setForm(null);
            }}
          >
            {t("Discard the edit")}
          </Button>
        </div>
      )}
      {!chain && loading && <p className="text-text-secondary">{t("Loading the chain…")}</p>}
      {chain && (
        <>
          {history && (
            <p className="text-small text-text-secondary">
              {t(
                "History in the order it was recorded; the number is each entry's place in the chain.",
              )}
            </p>
          )}
          <div>
            <Button size="sm" disabled={frozen} onClick={() => open("insert", null)}>
              {t("Insert at the start")}
            </Button>
            {form?.mode === "insert" && form.entryId === null && editForm(form)}
          </div>
          {chain.effective.length === 0 && (
            <p className="text-text-secondary">
              {t("The chain is empty. Insert the first step, or run the agent.")}
            </p>
          )}
          <ol
            aria-label={history ? t("Reasoning chain history") : t("Effective reasoning chain")}
            className="flex flex-col gap-2"
          >
            {rows.map((entry) => {
              const live = entry.position >= 0;
              const replacement = entry.supersededByRecordId
                ? byRecord.get(entry.supersededByRecordId)
                : undefined;
              const isEdit = entry.revisionOfId !== null || entry.insertedAfterId !== null;
              const long = entry.summary.length > PREVIEW_CHARS;
              return (
                <li
                  key={entry.id}
                  className={`rounded border border-divider p-2 ${live ? "" : "opacity-60"}`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold tabular-nums">
                      {live ? `#${entry.position + 1}` : "—"}
                    </span>
                    <span className={chip}>{kindLabel(entry)}</span>
                    {isEdit && <span className={chip}>{t("edit")}</span>}
                    {entry.truncated && (
                      <span className="text-small text-text-secondary">{t("shortened")}</span>
                    )}
                    {!entry.eligible && (
                      <span className="text-small text-text-secondary">
                        {t("no longer eligible")}
                      </span>
                    )}
                    {entry.evidence.length > 0 && (
                      <span className="text-small text-text-secondary">
                        {t("Evidence: {{n}}", { n: entry.evidence.length })}
                      </span>
                    )}
                  </div>
                  {long ? (
                    <details>
                      <summary className="whitespace-pre-wrap break-words">
                        {snippet(entry.summary, PREVIEW_CHARS)}{" "}
                        <span className="text-small text-text-secondary">
                          {t("Show the full text")}
                        </span>
                      </summary>
                      <p className="whitespace-pre-wrap break-words">{entry.summary}</p>
                    </details>
                  ) : (
                    <p className="whitespace-pre-wrap break-words">{entry.summary}</p>
                  )}
                  {entry.rationale && (
                    <p className="text-small text-text-secondary">
                      {t("Rationale: {{text}}", { text: entry.rationale })}
                    </p>
                  )}
                  {!live && entry.struck && (
                    <p className="text-small">
                      {t("Struck")}
                      {replacement?.rationale
                        ? ` · ${t("Reason: {{reason}}", { reason: replacement.rationale })}`
                        : ""}
                    </p>
                  )}
                  {!live && !entry.struck && replacement && (
                    <p className="text-small">
                      {t("Replaced by: {{summary}}", { summary: snippet(replacement.summary) })}
                    </p>
                  )}
                  {live && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button size="sm" disabled={frozen} onClick={() => open("replace", entry)}>
                        {t("Edit")}
                      </Button>
                      <Button size="sm" disabled={frozen} onClick={() => open("insert", entry)}>
                        {t("Insert after")}
                      </Button>
                      <Button size="sm" disabled={frozen} onClick={() => open("strike", entry)}>
                        {t("Strike")}
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={frozen || !launch}
                        onClick={() => void continueFrom(entry.id)}
                      >
                        {t("Continue from here")}
                      </Button>
                    </div>
                  )}
                  {form && form.entryId === entry.id && editForm(form)}
                </li>
              );
            })}
          </ol>
          <p className="text-small text-text-secondary">
            {t("Chain digest {{digest}} · session revision {{revision}}", {
              digest: chain.digest.slice(0, 12),
              revision: chain.sessionRevision,
            })}
          </p>
        </>
      )}
    </section>
  );
}
